import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import * as Icons from "lucide-react";

const SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "architecture", label: "Engine & Architecture" },
  { id: "world", label: "2.5D Rendering" },
  { id: "battles", label: "Battle System" },
  { id: "stats", label: "Stats, IVs & EVs" },
  { id: "progression", label: "Leveling & Evolution" },
  { id: "pokedex", label: "PokéDex & Shinies" },
  { id: "dungeons", label: "Procedural Dungeons" },
  { id: "items", label: "Items & Catch Math" },
  { id: "storage", label: "Box Storage" },
  { id: "quests", label: "Scripting Engine" },
  { id: "followers", label: "Companions" },
  { id: "saves", label: "Saves & PWA" },
  { id: "wallet", label: "Wallet & Sessions" },
  { id: "controls", label: "Controls" },
  { id: "faq", label: "FAQ" },
];

const DOCS = [
  {
    id: "overview",
    icon: "BookOpen",
    title: "Overview",
    body: [
      "PokéRealms is a fully client-side, browser-based monster-catching RPG rendered in hybrid 2.5D. The overworld is a real-time 3D scene; battles, menus, and HUD use a 2D sprite-based layer composited on top. There is no game server — all logic, state, and simulation run locally in your browser.",
      "Connect a Solana Phantom wallet to play as a verified trainer (sessions attributed to your address) or jump in instantly as a guest. The entire experience streams on demand with zero install.",
    ],
    specs: [
      { k: "Render mode", v: "Hybrid 2.5D (3D overworld + 2D battle/UI layer)" },
      { k: "Backend", v: "None — 100% client-side simulation" },
      { k: "Persistence", v: "Browser localStorage + JSON export/import" },
      { k: "Distribution", v: "Installable PWA, offline-capable" },
    ],
  },
  {
    id: "architecture",
    icon: "Cpu",
    title: "Engine & Architecture",
    body: [
      "PokéRealms is built on Svelte 5 with the runes reactivity model and TypeScript in strict mode. State is split across three reactive context stores — a global GameContext, a BattleContext, and an OverworldContext — each backed by Svelte writable stores and managed through a dependency-injection container so subsystems stay decoupled.",
      "Class-based domain models (the creature instances, moves, items, and dex entries) are kept separate from the Svelte view layer. UI is composed of 70+ components organized by domain: world, world3d, battle, menus, saves, and common primitives.",
    ],
    specs: [
      { k: "Framework", v: "Svelte 5 (runes: $state / $derived / $effect)" },
      { k: "Language", v: "TypeScript, strict mode, ESNext target" },
      { k: "Bundler", v: "Vite 7 with code-splitting" },
      { k: "3D layer", v: "Three.js via Threlte declarative components" },
      { k: "Animations", v: "GSAP timelines for battle & transitions" },
      { k: "Audio", v: "Howler.js with cached Howl instances + fade cleanup" },
      { k: "DI container", v: "tsyringe (decorator metadata, singletons)" },
      { k: "Grid math", v: "honeycomb-grid for tile/coordinate logic" },
      { k: "Charts", v: "Chart.js for stat/progress visualizations" },
    ],
  },
  {
    id: "world",
    icon: "Boxes",
    title: "2.5D Rendering",
    body: [
      "The overworld is a tile-based 3D scene. Terrain is built from instanced tile meshes, while characters, NPCs, trees, and props are rendered as billboard sprites — flat textured planes that are rotated every frame to always face the camera. This delivers crisp hand-authored sprite art inside a real 3D space with a dynamic, trailing camera.",
      "Sprites use transparent texture cutouts with depth-write disabled to avoid z-fighting against the ground plane, and animated sprite sheets drive idle, walk, and turn frames. A biome-aware lighting rig plus GPU weather particle systems (rain, snow, fog) and a real-time day/night cycle continuously re-light each region.",
    ],
    specs: [
      { k: "Entities", v: "Camera-facing billboard sprites on quad planes" },
      { k: "Z-fighting fix", v: "depthWrite:false + transparent cutouts + y-offset" },
      { k: "Performance", v: "Texture atlases + instancing to cut draw calls" },
      { k: "Lighting", v: "Per-biome lighting configuration" },
      { k: "Atmosphere", v: "Particle weather (rain/snow/fog) + day-night cycle" },
    ],
  },
  {
    id: "battles",
    icon: "Swords",
    title: "Battle System",
    body: [
      "Battles are turn-based and resolved with the canonical Gen 3+ damage model. Each turn orders actions by move priority and effective Speed, then applies the base damage formula followed by an ordered modifier chain (random roll, STAB, type effectiveness, then crits / items / weather / abilities), flooring at each step.",
      "The simulation models 150+ abilities, held items with battle effects, the full status/volatile system (burn, poison, paralysis, sleep, freeze, confusion), weather, terrain, and entry hazards. The opponent AI is not random: it scores candidate moves using threat estimation, type-matchup tables, and per-trainer personality profiles.",
    ],
    code:
`// Base damage (Gen 3+), floored at each modifier step
Damage = ((2 * Level / 5 + 2) * Power * A / D) / 50 + 2

// Ordered modifier chain
Final = floor(Damage
        * random(0.85 .. 1.00)   // 16 distinct damage rolls
        * STAB(1.5)              // if move type == attacker type
        * typeEffectiveness(0 | 0.25 | 0.5 | 1 | 2 | 4)
        * crit(2.0) * weather * item * ability)`,
    specs: [
      { k: "Abilities", v: "150+ implemented with battle hooks" },
      { k: "Type chart", v: "0 / 0.25 / 0.5 / 1 / 2 / 4 multipliers" },
      { k: "Damage rolls", v: "Random 85–100 → 16 possible rolls per hit" },
      { k: "Field state", v: "Weather, terrain, entry hazards, screens" },
      { k: "AI", v: "Threat-scoring + type matchups + personalities" },
    ],
  },
  {
    id: "stats",
    icon: "SlidersHorizontal",
    title: "Stats, IVs & EVs",
    body: [
      "Every creature instance carries six stats derived from species base stats, Individual Values (IVs, 0–31 per stat, fixed at generation), Effort Values (EVs, 0–255 per stat capped at 510 total), level, and a Nature multiplier. HP uses a distinct formula from the other five stats.",
      "Natures apply a 1.1× boost to one stat and a 0.9× reduction to another (neutral natures are 1.0×), applied as the final multiplier before flooring — letting trainers min-max competitive spreads.",
    ],
    code:
`HP   = floor((2*Base + IV + floor(EV/4)) * Level / 100) + Level + 10
Stat = floor((floor((2*Base + IV + floor(EV/4)) * Level / 100) + 5) * Nature)

// IV: 0..31  |  EV: 0..255 per stat, 510 total  |  Nature: 0.9 / 1.0 / 1.1`,
    specs: [
      { k: "IV range", v: "0–31 per stat (randomized at creation)" },
      { k: "EV range", v: "0–255 per stat, 510 total cap (4 EV = +1 pt)" },
      { k: "Natures", v: "1.1× boost / 0.9× hinder / 1.0× neutral" },
      { k: "Gender", v: "Computed from species gender ratio" },
    ],
  },
  {
    id: "progression",
    icon: "TrendingUp",
    title: "Leveling & Evolution",
    body: [
      "Experience is awarded after victories and mapped through growth-rate curves (Fast, Medium Fast, Medium Slow, Slow, Erratic, Fluctuating) — for example the Medium Fast group requires n³ total experience to reach level n. Leveling recalculates stats and unlocks level-up moves.",
      "Creatures evolve along their evolution chains via level thresholds, items, or special conditions. A Move Relearner NPC lets a creature recall any move it could have learned, so build flexibility is never permanently lost.",
    ],
    specs: [
      { k: "Growth groups", v: "Fast / Medium Fast / Medium Slow / Slow / Erratic / Fluctuating" },
      { k: "Medium Fast", v: "Total EXP for level n = n³" },
      { k: "Evolution", v: "Level, item, and condition-based chains" },
      { k: "Move relearner", v: "Recall any learnable move on demand" },
    ],
  },
  {
    id: "pokedex",
    icon: "ScrollText",
    title: "PokéDex & Shinies",
    body: [
      "The PokéDex tracks 600+ species with independent seen and caught flags, backed by a Map keyed on species id for O(1) lookups. Encountering a creature marks it seen; capturing it marks it caught, driving completion progress.",
      "Shiny (alternate-color) variants roll at an authentic rarity of 1 in 2048 per encounter, making each discovery a genuine trophy. Search results are returned through typed wrapper objects so unknown ids resolve safely.",
    ],
    specs: [
      { k: "Species", v: "600+ tracked entries" },
      { k: "Lookup", v: "Map<id, entry> → O(1) access" },
      { k: "Shiny rate", v: "1 / 2048 per encounter" },
      { k: "Tracking", v: "Independent seen + caught flags" },
    ],
  },
  {
    id: "dungeons",
    icon: "Castle",
    title: "Procedural Dungeons",
    body: [
      "Dungeon Mode generates floors procedurally with a seeded RNG, so a given seed reproduces the same layout, encounters, and rewards — enabling fair, repeatable runs. Each floor draws encounters from its biome tables and escalates in difficulty.",
      "Floors include rest rooms (where the Move Relearner appears), legendary rooms, and boss battles. A meta-progression layer carries persistent upgrades between runs so each descent compounds your power.",
    ],
    specs: [
      { k: "Generation", v: "Procedural floors with seeded RNG (deterministic)" },
      { k: "Encounters", v: "Biome-based encounter tables per floor" },
      { k: "Rooms", v: "Rest floors, legendary rooms, boss battles" },
      { k: "Meta", v: "Persistent between-run progression" },
    ],
  },
  {
    id: "items",
    icon: "Backpack",
    title: "Items & Catch Math",
    body: [
      "The bag holds consumables, capture balls, and held items; held items hook into the battle modifier chain (e.g. damage boosts, type-resist berries). Capturing applies the standard Gen 3+ catch formula, factoring current/max HP, the species catch rate, the ball's bonus, and any status condition, then resolves up to four shake checks.",
    ],
    code:
`a = floor((3*HPmax - 2*HPcur) * rate * ballBonus / (3*HPmax)) * statusBonus
// shake probability per check:
b = 65536 / (255 / a)^0.1875
// capture succeeds if all four shake checks pass (or a >= 255)`,
    specs: [
      { k: "Bag", v: "Consumables, capture balls, held items" },
      { k: "Held items", v: "Hook into the battle modifier chain" },
      { k: "Catch factors", v: "HP ratio, species rate, ball bonus, status" },
      { k: "Resolution", v: "Up to 4 shake checks per throw" },
    ],
  },
  {
    id: "storage",
    icon: "Database",
    title: "Box Storage",
    body: [
      "Beyond the active party, a PC box system stores your full collection across multiple boxes with drag-and-drop reorganization. Deposits, withdrawals, and swaps update the underlying save model immediately so your roster stays consistent.",
    ],
    specs: [
      { k: "Capacity", v: "Multiple boxes beyond the active party" },
      { k: "UX", v: "Drag-and-drop deposit / withdraw / swap" },
    ],
  },
  {
    id: "quests",
    icon: "MessageSquareText",
    title: "Scripting Engine",
    body: [
      "A scriptable engine powers NPC dialogues, quests, and map-triggered events. Scripts are composed from reusable scriptable primitives (dialogue, give-item, start-battle, move-relearner, conditional branches) and bound to map tiles or NPC interactions.",
      "Quests define objectives with completion tracking, and notifications surface progress to the player as objectives are met.",
    ],
    specs: [
      { k: "Primitives", v: "Composable scriptables (dialogue, branch, reward, battle)" },
      { k: "Triggers", v: "Tile-entry events + NPC interaction" },
      { k: "Quests", v: "Objective tracking with notifications" },
    ],
  },
  {
    id: "followers",
    icon: "PersonStanding",
    title: "Companions",
    body: [
      "A chosen creature from your party follows your trainer through the overworld as a billboard sprite, mirroring movement with a trailing path. It is purely a presentation feature layered over the same sprite and animation system used for the player.",
    ],
  },
  {
    id: "saves",
    icon: "Save",
    title: "Saves & PWA",
    body: [
      "PokéRealms supports multiple save slots persisted to browser localStorage, so several independent adventures can coexist. Saves can be exported to and imported from JSON files for backup or transfer between devices.",
      "The app ships as an installable Progressive Web App with a service worker, enabling add-to-home-screen and offline play once assets are cached.",
    ],
    specs: [
      { k: "Slots", v: "Multiple independent save slots" },
      { k: "Backup", v: "JSON export / import (download / upload)" },
      { k: "Storage", v: "Browser localStorage" },
      { k: "PWA", v: "Service worker, installable, offline-capable" },
    ],
  },
  {
    id: "wallet",
    icon: "Wallet",
    title: "Wallet & Sessions",
    body: [
      "PokéRealms integrates the Solana Phantom wallet through the injected browser provider. Connecting authorizes your public key and attributes each play session to that wallet address; guests can still play without connecting.",
      "While a session tab is visible, the client sends a heartbeat every 15 seconds to accumulate active play time. The community leaderboard ranks connected wallets by total time spent in-game, with one-click address copying in the admin view.",
    ],
    specs: [
      { k: "Wallet", v: "Solana Phantom (injected provider, public-key auth)" },
      { k: "Heartbeat", v: "15s ping while tab is visible (Visibility API)" },
      { k: "Leaderboard", v: "Connected wallets ranked by total playtime" },
      { k: "Guest mode", v: "Play without connecting a wallet" },
    ],
  },
  {
    id: "controls",
    icon: "Gamepad2",
    title: "Controls",
    body: [
      "On desktop, arrow keys drive movement while on-screen prompts handle interaction, menus, and battle confirmation. On touch devices, an on-screen virtual joystick plus action buttons provide full control. Input is routed through a dedicated commands layer that maps device events to game actions.",
    ],
    specs: [
      { k: "Desktop", v: "Arrow-key movement + on-screen action prompts" },
      { k: "Touch", v: "Virtual joystick + action buttons" },
      { k: "Layer", v: "Dedicated commands/input abstraction" },
    ],
  },
  {
    id: "faq",
    icon: "HelpCircle",
    title: "FAQ",
    body: [
      "Is PokéRealms free? Yes — it runs entirely in your browser at no cost.",
      "Do I need a wallet? No. Guest play is fully supported; connecting a Solana Phantom wallet is optional and links your sessions to the leaderboard.",
      "Where are saves stored? In your browser's localStorage. Export your save to JSON regularly if you want a portable backup.",
      "Does it work offline? Yes — once installed as a PWA and cached, PokéRealms can be played offline.",
    ],
  },
];

const Documents = () => {
  useEffect(() => {
    document.title = "PokéRealms Documents — Technical Features & Gameplay Guide";
    const setMeta = (name, content) => {
      let tag = document.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setMeta(
      "description",
      "Deep technical documentation for PokéRealms: a client-side 2.5D monster-catching RPG. Covers the Svelte/Three.js engine, Gen 3+ damage and stat formulas, IVs/EVs, procedural dungeons, catch math, wallet sessions, and more."
    );
    return () => {
      document.title = "PokéRealms";
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0b1626] text-white">
      <Navbar />
      <main className="pt-16">
        {/* Header */}
        <section className="section-gradient border-b border-white/5">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
            <p className="mono-pixel text-[#ffcb05] tracking-[0.3em] text-sm mb-3">// DOCUMENTS</p>
            <h1 className="pixel-font text-2xl sm:text-3xl lg:text-4xl text-white leading-relaxed" data-testid="documents-title">
              POKÉREALMS GUIDE
            </h1>
            <p className="text-white/60 mt-6 max-w-2xl text-base">
              A technical deep-dive into the systems that power PokéRealms — the engine, the battle math, procedural generation, persistence, and the wallet layer.
            </p>
            <Link to="/play" className="press-start pixel-font text-[10px] sm:text-xs px-5 py-3 rounded inline-block mt-8">
              START PLAYING
            </Link>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-[240px_1fr] gap-10">
          {/* Sidebar nav */}
          <aside className="hidden lg:block">
            <div className="sticky top-24">
              <p className="mono-pixel text-white/40 text-xs tracking-widest mb-4">ON THIS PAGE</p>
              <nav className="flex flex-col gap-1 border-l border-white/10">
                {SECTIONS.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="text-sm text-white/60 hover:text-[#ffcb05] hover:border-[#ffcb05] border-l-2 border-transparent -ml-px pl-4 py-1.5 transition-colors"
                    data-testid={`documents-nav-${s.id}`}
                  >
                    {s.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="space-y-14 max-w-3xl">
            {DOCS.map((doc) => {
              const Icon = Icons[doc.icon] || Icons.Sparkles;
              return (
                <article key={doc.id} id={doc.id} className="scroll-mt-24" data-testid={`documents-section-${doc.id}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#ffcb05]/15 border border-[#ffcb05]/30 shrink-0">
                      <Icon size={20} className="text-[#ffcb05]" />
                    </div>
                    <h2 className="pixel-font text-[#ffcb05] text-sm sm:text-base leading-relaxed">{doc.title.toUpperCase()}</h2>
                  </div>

                  <div className="space-y-4 pl-1">
                    {doc.body.map((p, i) => (
                      <p key={i} className="text-white/70 text-base leading-relaxed">{p}</p>
                    ))}
                  </div>

                  {doc.code && (
                    <pre className="mt-5 overflow-x-auto rounded-lg border border-[#ffcb05]/20 bg-[#070f1c] p-4 text-[13px] leading-relaxed text-[#9fe6c4] font-mono">
                      <code>{doc.code}</code>
                    </pre>
                  )}

                  {doc.specs && (
                    <dl className="mt-5 rounded-lg border border-white/10 bg-white/[0.03] divide-y divide-white/5">
                      {doc.specs.map((s, i) => (
                        <div key={i} className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 px-4 py-3">
                          <dt className="mono-pixel text-[#ffcb05]/90 text-xs tracking-wide sm:w-44 shrink-0 uppercase">{s.k}</dt>
                          <dd className="text-white/70 text-sm">{s.v}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Documents;
